// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { logout } from '../../../../store/globalActions';

import { useMediaQuery } from '../../../../hooks/useMediaQuery';

import { isUserAdmin } from '../../../../store/user/userSelectors';
import {
  selectFacilityEquipmentGroup,
  selectHasPartialLockByGroupId,
} from '../../../../store/equipment/equipmentSelectors';

import { UserCard } from '../../../User/UserCard/UserCard';

import { PopOver } from '../../../UI/PopOver/PopOver';

import { Button } from '../../../../../web-utils/src/UI/Component/React/V1/Button/Button';
import { LinkComponent } from '../../../../../web-utils/src/UI/Component/React/V1/LinkComponent/LinkComponent';
import { BooleanFieldState } from '../../../../../web-utils/src/UI/Component/React/V1/BooleanFieldState/BooleanFieldState';
import { ConfirmationDialog } from '../../../../../web-utils/src/UI/Component/React/V1/ConfirmationDialog/ConfirmationDialog';
import { useNodeActions } from '../../../../hooks/Facility/useNodeActions';

import styles from '../Navigation.module.css';
import button from '../../../UI/Button/Button.module.css';

const UnlockAllButton = ({ onClick }) => (
  <div className={styles.item}>
    <Button
      color='light'
      onClick={onClick}
      tooltipText='Unlock all equipment in the Facility'
      position='left'
      iconType='unlock'
      sizing
    >
      Unlock all
    </Button>
  </div>
);

export const UserNavigation = ({ adminLinks, isLocalUser }) => {
  const { user } = useSelector(state => state.user, shallowEqual);
  const { companies } = useSelector(state => state.companies);
  const isAdmin = useSelector(isUserAdmin, shallowEqual);
  const facilityEquipmentGroup = useSelector(selectFacilityEquipmentGroup, shallowEqual);
  const { hasPartialLock } =
    useSelector(
      state => selectHasPartialLockByGroupId(state, facilityEquipmentGroup?.equip_group_uuid),
      shallowEqual
    ) || {};

  const isMobile = useMediaQuery('(max-width: 768px)');

  const dispatch = useDispatch();

  const [confirmUnlockAll, setConfirmUnlockAll] = useState(false);
  const [discardPendingRequests, setDiscardPendingRequests] = useState(false);
  const { handleUnlockAll } = useNodeActions({
    node: facilityEquipmentGroup ? { ...facilityEquipmentGroup, type: 'equipmentGroup' } : null,
  });

  const handleConfirmUnlockAll = async () => {
    await handleUnlockAll({ discardPendingRequests });
    setConfirmUnlockAll(false);
    setDiscardPendingRequests(false);
  };

  const handleCancelUnlockAll = () => {
    setConfirmUnlockAll(false);
    setDiscardPendingRequests(false);
  };

  const handleClearUser = setIsOpen => {
    dispatch(logout(isLocalUser));
    setIsOpen(false);
  };

  return (
    <>
      <PopOver
        color=''
        iconType='user'
        fill='var(--color-light)'
        stroke='none'
        hoverEffect={true}
        classname={button.btnNavigation}
        buttonText={isMobile ? 'User' : null}
      >
        {({ setIsOpen }) => (
          <div className={styles.navItemContainer}>
            <div className={styles.padding}>
              <div className={styles.borderBottom}>
                <UserCard user={user} flexDirection='column' />
              </div>
              {!!companies?.length && (
                <div className={styles.borderBottom}>
                  {adminLinks.map(({ id, to, iconType, label }) => (
                    <LinkComponent
                      key={id}
                      href={to}
                      iconType={iconType}
                      stroke='var(--color-light)'
                      onClick={() => setIsOpen(false)}
                    >
                      {label}
                    </LinkComponent>
                  ))}
                </div>
              )}
              {isAdmin && facilityEquipmentGroup && hasPartialLock && (
                <div className={styles.borderBottom}>
                  <UnlockAllButton onClick={() => setConfirmUnlockAll(true)} />
                </div>
              )}
              <div className={styles.item}>
                <Button
                  color='light'
                  onClick={() => handleClearUser(setIsOpen)}
                  tooltipText={`Logout`}
                  position='left'
                  iconType='logout'
                  sizing
                >
                  Log out
                </Button>
              </div>
            </div>
          </div>
        )}
      </PopOver>
      {confirmUnlockAll &&
        createPortal(
          <ConfirmationDialog
            message={
              <>
                Unlock all equipment in {facilityEquipmentGroup?.name || 'the Facility'}?
                <br />
                <BooleanFieldState
                  val={discardPendingRequests}
                  label='Discard pending requests'
                  labelAfter
                  onChange={e => setDiscardPendingRequests(e.target.checked)}
                />
              </>
            }
            confirm='Confirm'
            cancel='Cancel'
            handleConfirm={handleConfirmUnlockAll}
            handleCancel={handleCancelUnlockAll}
          />,
          document.body
        )}
    </>
  );
};
